import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { markPageVisited } from '../../../../api/patient.api';
import { useAuth } from '../../../../context/auth-context';
import OraChatBot from '../../OraChatBot/OraChatBot';
import './TreatmentsTemplate.scss';

interface DecisionFactorsTemplateProps {
    pageTitle: string;
    pageSubTitle: string;
    pageDescription: React.ReactNode;
    videoKey: string;
    pageType: 'treatment' | 'criteria';
    resourceLinks?: {
        title: string;
        link: string;
    }[];
}

const TreatmentsTemplate = ({
    pageTitle,
    pageSubTitle,
    pageDescription,
    videoKey,
    resourceLinks,
    pageType,
}: DecisionFactorsTemplateProps) => {
    const navigate = useNavigate();
    const isRedirectedAfterLogin = useRef(false);
    const { isAuthLoading, isAuthenticated, userInfo } = useAuth();

    useEffect(() => {
        if (isAuthLoading || !isAuthenticated) {
            return;
        }

        if (isRedirectedAfterLogin.current) {
            return;
        }

        const savedURL = localStorage.getItem('preLoginURL') || '';
        localStorage.removeItem('preLoginURL');
        if (savedURL) {
            navigate(savedURL);
            isRedirectedAfterLogin.current = true;
            return;
        }

        if (userInfo?.role === 'patient') {
            markPageVisited({ pageName: pageTitle, pageType });
        }

        isRedirectedAfterLogin.current = true;
    }, [ isAuthLoading, isAuthenticated, navigate, userInfo, pageTitle, pageType ]);

    return (
        <div className="decision-factors-template">
            <OraChatBot pageTitle={pageTitle}/>

            <h1 className="page-title">{pageTitle}</h1>

            <section className="video-intro-section" id="video-introduction">
                <div className="container">
                    <div className="video-intro-content">
                        <div className="video-intro-player">
                            <iframe
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                id="main-video"
                                src={`https://www.youtube.com/embed/${videoKey}`}
                                title={pageTitle}
                            />
                        </div>
                        <div className="video-intro-text">
                            <div className="overview-text">
                                <p className="lead">{pageSubTitle}</p>
                                {pageDescription}
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {resourceLinks && resourceLinks.length > 0 && (
                <section className="resources-section" id="resources">
                    <div className="container">
                        <h2 className="section-title">Learn More from Trusted Sources</h2>
                        <div className="resources-list">
                            {resourceLinks?.map((item) => (
                                <div className="resource-item" key={item.link}>
                                    <p>
                                        <a href={item.link} rel="noopener noreferrer" target="_blank">
                                            {item.title}
                                        </a>
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default TreatmentsTemplate;